// @ts-strict-ignore
import React, {
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AggregationEditorContext,
  AggregationsSubEditorContainer,
} from 'flow/editor/components/aggregations';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { resetSubEditor } from 'flow/store/redux/thunks';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import type { AggregationSourceData } from 'flow/editor/components/aggregations/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from 'flow/editor/components/aggregations/transformers';
import { getIsSaveDisabled } from 'flow/editor/components/aggregations/helpers';
import { selectRuleBuilderActiveSubEditor, selectWorkflowProps } from 'flow/store/redux/selectors';
import { RuleBuilderSubEditorSide } from 'flow/store/engine/types/utils';
import { getIsPreview } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { getMultiSelectProps } from './helpers';
import { updateThreshold as updateThresholdSlice } from 'flow/store/redux/slice';

const AggregationsWrapper = () => {
  const dispatch = useAppDispatch();
  const { populatedThreshold } = useContext(ThresholdContext);
  const { keyboardFocus, editMode } = useContext(RuleBuilderContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { aggregationConfig: aggregationConfigByObjectType } = useAppSelector(selectWorkflowProps);

  const side = activeSubEditor.side === RuleBuilderSubEditorSide.LHS ? 'left' : 'right';
  const attribute = populatedThreshold[side]?.attribute;

  const aggregationConfig = useMemo(
    () => getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType),
    [attribute, aggregationConfigByObjectType],
  );

  const multiSelectProps = useMemo(() => getMultiSelectProps({
    action: populatedThreshold[side].action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  }), [populatedThreshold, side, editMode, activeSubEditor.thresholdId]);

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [aggregationContextSourceData, setAggregationContextSourceData] = useState<
  AggregationSourceData
  >(() => aggregationComponentsToSourceData(
    populatedThreshold[side].aggregationComponents,
  ));

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(aggregationContextSourceData),
    [aggregationContextSourceData],
  );

  const aggregationEditorContext = useMemo(() => {
    const handleClose = () => {
      dispatch(resetSubEditor());
      keyboardFocus.enable();
    };

    return {
      sourceData: aggregationContextSourceData,
      setSourceData: setAggregationContextSourceData,
      handleSave: () => {
        dispatch(updateThresholdSlice({
          path: `${populatedThreshold.publicId}.${side}.aggregationComponents`,
          value: sourceDataToAggregationComponents(
            aggregationContextSourceData,
            populatedThreshold[side].aggregationComponents,
          ),
        }));
        handleClose();
      },
      handleCancel: () => {
        setAggregationContextSourceData(aggregationComponentsToSourceData(
          populatedThreshold[side].aggregationComponents,
        ));
        handleClose();
      },
      aggregationEditorContainerRef,
      multiSelectProps,
      aggregationConfig,
      isSaveDisabled,
      validationErrors: {},
    };
  }, [
    dispatch,
    keyboardFocus,
    aggregationContextSourceData,
    populatedThreshold,
    side,
    multiSelectProps,
    aggregationConfig,
    isSaveDisabled,
  ]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationsWrapper;
